// src/config/stripe.ts
// Centralized Stripe configuration for web application

/**
 * Get Stripe publishable key from environment
 * Falls back to test key for development only
 * 
 * IMPORTANT: Set REACT_APP_STRIPE_PUBLIC_KEY in your environment:
 * - Development: .env.development  → pk_test_...
 * - Staging:     Vercel env vars   → pk_test_... (MUST match the backend sk_test_ key)
 * - Production:  Vercel env vars   → pk_live_... (MUST match the backend sk_live_ key)
 *
 * A mismatch (e.g. pk_live_ frontend + sk_test_ backend) will cause "No such setupintent"
 * errors because Stripe SetupIntents are scoped to a single key-pair account/mode.
 */
export const getStripePublishableKey = (): string => {
  const envKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

  // If environment key is set, use it
  if (envKey && envKey.trim() !== '') {
    return envKey;
  }

  // A real deployed build (Vercel Production or Preview sets NODE_ENV=production at
  // build time) must never silently downgrade to a test key. Fail loudly instead —
  // set REACT_APP_STRIPE_PUBLIC_KEY in Vercel before deploying.
  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      '[Stripe Config] REACT_APP_STRIPE_PUBLIC_KEY is not set for this production build. ' +
        "Refusing to silently fall back to a test key — set REACT_APP_STRIPE_PUBLIC_KEY " +
        "in this Vercel project/environment's Environment Variables before deploying."
    );
  }

  // Fallback to test key for local development / test runs only.
  // This is a public test key and is safe to include in code.
  const fallbackTestKey = 'pk_test_KmC67iQJDTfxelGyp5DKexjV';
  console.log('[Stripe Config] Using fallback test key for development');
  return fallbackTestKey;
};

/**
 * Check if Stripe is properly configured
 */
export const isStripeConfigured = (): boolean => {
  const envKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  return envKey !== undefined && envKey.trim() !== '';
};

/**
 * Get Stripe key type (test or live)
 */
export const getStripeKeyType = (): 'test' | 'live' | 'unknown' => {
  const key = getStripePublishableKey();
  
  if (key.startsWith('pk_test_')) {
    return 'test';
  } else if (key.startsWith('pk_live_')) {
    return 'live';
  }
  
  return 'unknown';
};

/**
 * Validate Stripe configuration on app startup
 */
export const validateStripeConfig = (): void => {
  const keyType = getStripeKeyType();
  const env = process.env.NODE_ENV;
  
  console.log('[Stripe Config] Environment:', env);
  console.log('[Stripe Config] Key Type:', keyType);
  
  // Warn if using test key in production
  if (env === 'production' && keyType === 'test') {
    console.error('[Stripe Config] ⚠️  WARNING: Using test Stripe key in production environment!');
  }
  
  // Success message
  if (keyType === 'live' && env === 'production') {
    console.log('[Stripe Config] ✅ Production Stripe configuration valid');
  } else if (keyType === 'test' && env !== 'production') {
    console.log('[Stripe Config] ✅ Development Stripe configuration valid');
  }
};

const stripeConfig = {
  getStripePublishableKey,
  isStripeConfigured,
  getStripeKeyType,
  validateStripeConfig,
};

export default stripeConfig;

